import { Component, OnInit, ChangeDetectionStrategy, OnDestroy, ViewEncapsulation, computed } from '@angular/core';
import { PaymentService } from '../services/payment.service';
import { filter, first, map, takeUntil, tap } from 'rxjs/operators';
import { BehaviorSubject, Observable, Subject, Subscription, combineLatest, from, interval } from 'rxjs';
import { MsalHelperService } from '../services/msal-helper.service';
import { ActivatedRoute } from '@angular/router';
import { ApiService } from '../services/api.service';
import { distinct, groupBy } from '../services/flow-management.service';
import { DataItem } from '@progress/kendo-angular-grid';
import { FlowRun } from '../obj/FlowRun';
import { Flow } from '../obj/Flow';
import { ObservableQuery, OperationVariables } from '@apollo/client/core';
import { ContextService } from '../services/context.service';

@Component({
    selector: 'app-teams',
    templateUrl: './teams.component.html',
    styleUrls: ['./teams.component.scss'],
    changeDetection: ChangeDetectionStrategy.Default,
    encapsulation: ViewEncapsulation.Emulated,
    standalone: false
})
export class TeamsComponent implements OnInit, OnDestroy {

  // public authUrl = '/server/login';

  private subscription = new Subscription();
  public license$ = new BehaviorSubject(null);
  public user$ = new BehaviorSubject(null);
  scans$ = new BehaviorSubject([]);
  scansTimespan$: Observable<any>;
  public accounts$ = new BehaviorSubject([]);
  public monitored$ = new BehaviorSubject([]);
  public contextState: 'loading' | 'noStorage' | 'noLicense' | 'ready' = 'loading';
  private destroy$ = new Subject();

  public flowChartData$ = new BehaviorSubject([]);
  public runs$: BehaviorSubject<FlowRun[]> = new BehaviorSubject([]);
  public runChartData$ = new BehaviorSubject([]);

  public periodStart$ = new BehaviorSubject<Date>(null);
  public periodEnd$ = new BehaviorSubject<Date>(null);

  public rules$ = new BehaviorSubject([]);
  public workspace;
  public workspaceFlowCapacity;

  // public data = [
  //   {
  //     kind: "Hydroelectric",
  //     share: 0.175,
  //   },
  //   {
  //     kind: "Nuclear",
  //     share: 0.238,
  //   },
  //   {
  //     kind: "Coal",
  //     share: 0.118,
  //   },
  //   {
  //     kind: "Solar",
  //     share: 0.052,
  //   },
  //   {
  //     kind: "Wind",
  //     share: 0.225,
  //   },
  //   {
  //     kind: "Other",
  //     share: 0.192,
  //   },
  // ];

  constructor(private paymentService: PaymentService, private msalHelperService: MsalHelperService, private monitorService: ApiService, private route: ActivatedRoute, private contextService: ContextService) { 

    this.workspace = this.paymentService.workspace;
    this.workspaceFlowCapacity = computed(() =>
      (this.workspace() || []).reduce((sum, ws) => sum + ws.flow_capacity, 0)
    );

    this.subscription.add(
      route.queryParamMap.subscribe((params)=>{
        const op = params.get('op');
        const table = params.get('table');

        if (op && table) {
          this.downloadCsv(table);
        }

      })  
    );

  }

  ngOnInit(): void {

    this.subscription.add(
      this.msalHelperService.user$.subscribe((user) => {
        if (user) this.user$.next(user);
      })
    );

    this.contextService.load().then((ctx) => {
      if (!ctx?.workspace) {
        this.contextState = 'noLicense';
        return;
      }
      if (!ctx.hasWorkspaceStorage) {
        this.contextState = 'noStorage';
        return;
      }
      if (!ctx.hasTeamsEntitlement) {
        this.contextState = 'noLicense';
        return;
      }

      this.license$.next(ctx.clarityUser);
      this.accounts$.next(ctx.accounts);
      this.contextState = 'ready';

      from(this.monitorService.watchConfigurations().then((query) => {
        this.subscription.add(
          query.subscribe((result) => {
            const configs = result.data;
            this.rules$.next(configs.rules);
          })
        );
      }));

      this.subscription.add(
        this.monitorService.run$.subscribe((runs) => {
          if (runs === null) {
            this.monitorService.watchRuns(-7).then(({ start, end }) => {
              this.periodEnd$.next(end);
              this.periodStart$.next(start);
            });
            return;
          }
        })
      );
      this.subscription.add(
        combineLatest([this.monitorService.flow$, this.monitorService.run$]).subscribe(
        ([flows, runs])=>{

          if (flows === null) {
            return;
          }
          if (runs === null) {
            return;
          }

          this.runs$.next(
            (runs||[]).map((run=>{
              return new FlowRun(run);
            }))
          );

          const groupedRuns = groupBy(runs, 'status');

          const groupedFlowRuns = groupBy(runs, 'flowName');

          const statuses = (runs || []).map((r)=>r?.['status']).filter(distinct);

          const runChartData = statuses.map((s)=>({
            'status': s,
            'count': (groupedRuns[s] || []).length,
            'colour': this.getColour(s)
          }));

          const monitored = flows.filter((f)=> f.name && f.monitor).map((f)=>{

            // try return a health rating
            const flowRuns = groupedFlowRuns[f.name] || [];
            const groupedRuns = groupBy(flowRuns, 'status');

            f.periodRuns = flowRuns.length;
            f.periodSuccessed = (groupedRuns['Succeeded'] || []).length;
            f.periodFailed = (groupedRuns['Failed'] || []).length;
            f.periodCancelled = (groupedRuns['Cancelled'] || []).length;

            f.subState = f.state;
            if (f.periodRuns == 0) {
              f.subState = `${f.state}:No Runs`;
              return f;
            }

            const success = f.periodSuccessed / f.periodRuns;
            f.periodFailedRate = f.periodFailed / f.periodRuns;
            // const fail = (runs.length - success) / runs.length;

            switch(true){
              case (success > 0.95):
                f.subState = `${f.state}:Healthy`;
                break;
              case (success > 0.60):
                f.subState = `${f.state}:Warning`;
                break;
              default:
                f.subState = `${f.state}:Critical`;
                break;
            }
            return f;
          });

          this.monitored$.next(monitored);
          
          const groupedFlows1 = groupBy(monitored, 'subState');
          // const groupedFlows2 = groupBy(flows, 'state');
  
          const states = (flows || []).map((f)=>{

            return f.subState;
          }).filter(distinct);  

          const chartFlowData = states.map((s)=>({
            'state': s,
            'count': (groupedFlows1[s] || []).length,
            'colour': this.getColour(s)
          }));
  
          this.flowChartData$.next(chartFlowData);

          this.runChartData$.next(runChartData);


        })
    );

    this.refreshStats();

    this.scansTimespan$ = combineLatest([interval(1000), this.scans$]).pipe(map(([t, scans])=>{
      return (scans || []).map((stats)=>{
        try {
          const start = new Date(stats.start).valueOf();
          const end = stats.ended ? new Date(stats.end).valueOf() : new Date().valueOf();
    
          const diffInMs = Math.abs(end - start);
          let diffInMins = Math.floor(diffInMs / 1000 / 60);
          const diffInSecs = Math.floor((diffInMs / 1000) % 60);
    
          return `${diffInMins} minutes and ${diffInSecs} seconds`;  
        }
        catch(error) {
          return `... minutes and ... seconds`;
        }      
      })
    }));
    this.subscription.add(this.scansTimespan$.subscribe());

    }).catch(() => {
      this.contextState = 'noLicense';
    });

  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  public refreshStats() {
    this.monitorService.queryScans().then((scans) => {
      this.scans$.next(scans);

      if (!scans) {
        // query again
        setTimeout(()=>{
          this.refreshStats();
        },30000);
      }
    });
  }

  public findAccount(oid){
    return this.accounts$.value.find((a)=>a.oid == oid);
  }

  public encodeState(state, cds=null) {
    const {workspace} = this.license$.getValue();

    let o = {
      "s": state,
      "ws": workspace,
      cds,
      "r": `${window.location.href}`
    };

    return btoa(JSON.stringify(o));
  }

  public labelContent(e): string {
    return `${e.value} (${(e.percentage * 100).toFixed(2)}%)`;
  }

  private getColour(category: any) {
    switch(category){
      case "Succeeded":
        return "#78d237";
      case "Running":
        return "#ffd246";
      
      case "Started:Healthy":
        return "#78d237";
      case "Started:Warning":
        return "#ffd246";
      case "Started:No Runs":
        return "#ffd246";
      case "Started:Critical":
        return "#ff6358";
      case "Stopped:No Runs":
        return "#ff6358";

      default:
        return "#ff6358";
    }
  }

  public filterByFlowState() {

  }

  public downloadCsv(table = 'flowruns') {
    this.monitorService.downloadCsv(table);
  }

  "#ff6358" //red
  "#ffd246" //yellow
  "#78d237" //green
  "#28b4c8" //cyan
  "#2d73f5" //blue
  "#aa46be" //purple
}

